import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { AuthContext } from "../context/AuthContext";
import "./styles/Part.css"; // re-use shared styles

export default function PartManager() {
  const { user } = useContext(AuthContext);

  // ---- Role helpers (case-insensitive)
  const role = String(user?.role || "").toLowerCase();
  const canAdd = role === "admin" || role === "super-admin" || role === "manager";
  const canEdit = role === "admin" || role === "super-admin";
  const canToggle = role === "admin" || role === "super-admin" || role === "manager";
  const canDelete = role === "admin";
  const canViewHistory = canToggle || canEdit || canDelete; // managers & above

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // list state
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [limit, setLimit] = useState(100);

  // form state
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ key: "", label: "", priceINR: "", isActive: true });

  // bulk upsert state
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState(
    '[  { "key": "pump", "label": "Pump", "priceINR": 3500 },  { "key": "filter", "label": "Filter Cartridge", "priceINR": 450 }]'
  );
  const [busy, setBusy] = useState(false);

  // history modal state
  const [showHistory, setShowHistory] = useState(false);
  const [historyPart, setHistoryPart] = useState(null); // { _id, key, label }
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchParts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      if (includeInactive) params.append("includeInactive", "true");
      if (limit) params.append("limit", String(limit));
      const res = await axios.get(`/api/parts?${params.toString()}`, authHeader);
      setParts(res.data || []);
    } catch (err) {
      console.error("Failed to load parts", err);
      alert("Failed to load parts: " + (err?.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({ key: "", label: "", priceINR: "", isActive: true });
  };

  const downloadExcel = () => {
    if (!parts || parts.length === 0) {
      alert("No parts to export!");
      return;
    }

    const data = parts.map((p, i) => ({
      "S.No.": i + 1,
      Key: p.key,
      Label: p.label,
      "Price (₹)": p.priceINR,
      Status: p.isActive !== false ? "Active" : "Inactive",
      "Created By": p.addedByName || p.addedBy || "unknown",
      "Created At": p.createdAt ? new Date(p.createdAt).toLocaleString() : "",
      "Updated By": p.updatedByName || "",
      "Updated At": p.updatedAt ? new Date(p.updatedAt).toLocaleString() : ""
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Parts");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    saveAs(blob, `PartsCatalog_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.key || !form.label || form.priceINR === "") {
      return alert("Please fill key, label and price");
    }
    try {
      setBusy(true);
      const payload = {
        key: String(form.key).toLowerCase().trim(),
        label: form.label,
        priceINR: Number(form.priceINR),
        isActive: !!form.isActive
      };
      if (editingId) {
        if (!canEdit) return alert("You do not have permission to edit parts.");
        await axios.put(`/api/parts/${editingId}`, payload, authHeader);
      } else {
        if (!canAdd) return alert("You do not have permission to add parts.");
        await axios.post("/api/partsadd", payload, authHeader);
      }
      resetForm();
      fetchParts();
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 409) {
        alert(`Duplicate key: ${String(form.key).toLowerCase().trim()}`);
      } else {
        alert("Save failed: " + (err?.response?.data?.message || err.message));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = (p) => {
    setEditingId(p._id);
    setForm({
      key: p.key,
      label: p.label,
      priceINR: p.priceINR,
      isActive: p.isActive !== false
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleToggleActive = async (p) => {
    if (!canToggle) return;
    try {
      setBusy(true);
      await axios.patch(
        `/api/parts/${p._id}/status`,
        { isActive: !p.isActive },
        authHeader
      );
      fetchParts();
    } catch (err) {
      console.error(err);
      alert("Failed to update status: " + (err?.response?.data?.message || err.message));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (p) => {
    if (!canDelete) return;
    const hard = window.confirm(
      "Hard delete? This cannot be undone. Click OK for hard delete, Cancel to just deactivate."
    );
    try {
      setBusy(true);
      if (hard) {
        await axios.delete(`/api/parts/${p._id}?hard=true`, authHeader);
      } else {
        await axios.delete(`/api/parts/${p._id}`, authHeader); // soft delete (deactivate)
      }
      fetchParts();
    } catch (err) {
      console.error(err);
      alert("Delete failed: " + (err?.response?.data?.message || err.message));
    } finally {
      setBusy(false);
    }
  };

  // Bulk helpers: accept JSON array or CSV (key,label,priceINR,isActive)
  const parseBulk = (text) => {
    try {
      const json = JSON.parse(text);
      if (Array.isArray(json)) return json;
    } catch (_) {}

    const rows = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l && !l.startsWith("#"));
    if (!rows.length) return [];

    let start = 0;
    const first = rows[0].split(",").map((c) => c.trim().toLowerCase());
    if (first[0] === "key" && first[1] === "label") {
      start = 1;
    }

    const items = [];
    for (let i = start; i < rows.length; i++) {
      const cols = rows[i].split(",").map((c) => c.trim());
      if (!cols[0] || !cols[1]) continue;
      items.push({
        key: cols[0].toLowerCase(),
        label: cols[1],
        priceINR: Number(cols[2] || 0),
        isActive: cols[3] ? /^true|1|yes$/i.test(cols[3]) : true
      });
    }
    return items;
  };

  const handleBulkUpsert = async () => {
    if (!canEdit && !canDelete && !canAdd) return; // guard
    if (!role || !(role === "admin" || role === "super-admin")) {
      // bulk is admin-only per routes
      return alert("Only admins can bulk upsert parts.");
    }
    try {
      setBusy(true);
      const items = parseBulk(bulkText);
      if (!items.length) {
        alert("No valid items found in bulk input");
        return;
      }
      await axios.post("/api/parts/bulk", { items }, authHeader);
      setShowBulk(false);
      setBulkText(
        '[{ "key": "pump", "label": "Pump", "priceINR": 3500 },   { "key": "filter", "label": "Filter Cartridge", "priceINR": 450 }]'
      );
      fetchParts();
    } catch (err) {
      console.error(err);
      alert("Bulk upsert failed: " + (err?.response?.data?.message || err.message));
    } finally {
      setBusy(false);
    }
  };

  // History modal handlers
  const openHistory = async (part) => {
    if (!canViewHistory) return;
    setHistoryPart(part);
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`/api/parts/${part._id}/history`, authHeader);
      const items = Array.isArray(res.data?.history) ? res.data.history : [];
      // Reverse to show latest first
      setHistoryItems([...items].reverse());
    } catch (e) {
      alert("Failed to load history: " + (e.response?.data?.message || e.message));
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setShowHistory(false);
    setHistoryPart(null);
    setHistoryItems([]);
  };

  return (
    <div className="main-content">
    <div className="tech-page">
      <h2 className="page-title">Parts Catalog</h2>

      {/* TOP: Create / Edit */}
      <section className="card">
        <div className="card-title">{editingId ? "Edit Part" : "Add Part"}</div>
        {!canAdd && !editingId && (
          <div className="muted">You have read-only access. Only managers/admins can add parts.</div>
        )}
        {editingId && !canEdit && (
          <div className="muted">Only admins can edit parts. You can cancel the edit below.</div>
        )}

        <form className="form" onSubmit={handleSubmit}>
          <div className="row-2">
            <div className="form-group">
              <label>Key</label>
              <input
                type="text"
                placeholder="e.g. pump"
                value={form.key}
                onChange={(e) => setForm((p) => ({ ...p, key: e.target.value }))}
                disabled={(!canAdd && !editingId) || !!editingId} // lock key on edit
                required
              />
              {editingId && (
                <span className="muted">Key cannot be changed for an existing part.</span>
              )}
            </div>

            <div className="form-group">
              <label>Label</label>
              <input
                type="text"
                placeholder="e.g. Pump"
                value={form.label}
                onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                disabled={!canEdit && !!editingId}
                required
              />
            </div>
          </div>

          <div className="row-2">
            <div className="form-group">
              <label>Price (₹)</label>
              <input
                type="number"
                min={0}
                step={0.01}
                placeholder="0"
                value={form.priceINR}
                onChange={(e) => setForm((p) => ({ ...p, priceINR: e.target.value }))}
                disabled={!canEdit && !!editingId}
                required
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                value={String(form.isActive)}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.value === "true" }))}
                disabled={!canEdit && !!editingId}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="row gap">
            <button
              type="submit"
              className="btn primary"
              disabled={busy || (!canAdd && !editingId) || (editingId && !canEdit)}
            >
              {busy ? "Saving…" : editingId ? "Update" : "Add"}
            </button>
            {editingId && (
              <button type="button" className="btn" onClick={resetForm} disabled={busy}>
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </section>
      <br></br>

      {/* BULK: Upsert (admin only) */}
      <section className="card">
        <div className="row-between">
          <div className="card-title">Bulk Upsert</div>
          <button className="btn" onClick={() => setShowBulk((s) => !s)}>
            {showBulk ? "Hide" : "Show"}
          </button>
        </div>
        {showBulk && (
          <div className="form" style={{ marginTop: 8 }}>
            <div className="muted">Paste a JSON array OR CSV (key,label,priceINR,isActive)</div>
            <textarea
              rows={8}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              className="form-group"
              style={{ width: "100%" }}
              disabled={busy || !(role === "admin" || role === "super-admin")}
            />
            <div className="row gap">
              <button
                className="btn primary"
                onClick={handleBulkUpsert}
                disabled={busy || !(role === "admin" || role === "super-admin")}
              >
                Run Bulk Upsert
              </button>
              <button className="btn" onClick={() => setBulkText("")} disabled={busy}>
                Clear
              </button>
            </div>
          </div>
        )}
      </section>
      <br></br> 
      {/* LIST */}
      <section className="card">
        <div className="card-title">Parts</div>
        <div className="filters">
          <div className="row gap">
            <input
              type="text"
              placeholder="Search key or label…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ maxWidth: 320 }}
            />
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
              {[25, 50, 100, 200, 500].map((n) => (
                <option key={n} value={n}>
                  {n} rows
                </option>
              ))}
            </select>
            <label className="row gap" style={{ alignItems: "center" }}>
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              <span>Include Inactive</span>
            </label>
            <button className="btn" onClick={fetchParts}>
              {loading ? "Loading…" : "Refresh"}
            </button>
            <button className="btn primary" onClick={downloadExcel}>
              Download Excel
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Label</th>
                <th>Price (₹)</th>
                <th>Status</th>
                <th>Created By</th>
                <th>Created At</th>
                <th>Updated By</th>
                <th>Updated At</th>
                {(canEdit || canToggle || canDelete) && <th style={{ width: 280 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {parts.map((p) => (
                <tr key={p._id}>
                  <td className="nowrap">{p.key}</td>
                  <td>{p.label}</td>
                  <td className="nowrap">₹ {Number(p.priceINR || 0).toFixed(2)}</td>
                  <td>
                    {p.isActive !== false ? (
                      <span className="badge">Active</span>
                    ) : (
                      <span
                        className="badge"
                        style={{ background: "#fee2e2", color: "#991b1b", borderColor: "#fecaca" }}
                      >
                        Inactive
                      </span>
                    )}
                  </td>
                  <td>{p.addedByName || p.addedBy || <span className="muted">unknown</span>}</td>
                  <td className="nowrap">
                    {p.createdAt ? new Date(p.createdAt).toLocaleString() : ""}
                  </td>
                  <td>{p.updatedByName || ""}</td>
                  <td className="nowrap">
                    {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : ""}
                  </td>
                  {(canEdit || canToggle || canDelete) && (
                    <td className="nowrap">
                      <div className="row gap">
                        {canEdit && (
                          <button className="btn" onClick={() => handleEdit(p)} disabled={busy}>
                            Edit
                          </button>
                        )}
                        {canToggle && (
                          <button
                            className="btn"
                            onClick={() => handleToggleActive(p)}
                            disabled={busy}
                          >
                            {p.isActive !== false ? "Deactivate" : "Activate"}
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn"
                            onClick={() => handleDelete(p)}
                            disabled={busy}
                          >
                            Delete
                          </button>
                        )}
                        {canViewHistory && (
                          <button className="btn" onClick={() => openHistory(p)} disabled={busy}>
                            History
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {!loading && parts.length === 0 && (
                <tr>
                  <td colSpan={9} className="muted center">
                    No parts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* History Modal */}
      {showHistory && (
        <div
          className="modal-overlay"
          onClick={closeHistory}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              width: "min(760px, 94vw)",
              borderRadius: 14,
              padding: "16px 18px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              maxHeight: "80vh",
              overflow: "auto"
            }}
          >
            <div
              className="modal-header"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            >
              <h3>
                History — {historyPart?.label} ({historyPart?.key})
              </h3>
              <button className="btn small" onClick={closeHistory}>
                Close
              </button>
            </div>

            {historyLoading ? (
              <div className="muted">Loading history…</div>
            ) : historyItems.length === 0 ? (
              <div className="muted">No history found.</div>
            ) : (
              <ul className="timeline" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {historyItems.map((h, idx) => (
                  <li
                    key={idx}
                    className={`timeline-item action-${h.action}`}
                    style={{ padding: "10px 0", borderBottom: "1px solid #eee" }}
                  >
                    <div
                      className="timeline-row"
                      style={{ display: "flex", gap: 8, alignItems: "baseline" }}
                    >
                      <strong className="action" style={{ textTransform: "capitalize" }}>
                        {h.action}
                      </strong>
                      <span className="when" style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>
                        {new Date(h.at || Date.now()).toLocaleString()}
                      </span>
                    </div>
                    <div className="timeline-sub" style={{ fontSize: 13, color: "#444", margin: "2px 0 6px" }}>
                      by <span className="by">{h.byName || "unknown"}</span>
                    </div>

                    {h.details && (
                      <pre
                        className="details"
                        style={{
                          margin: 0,
                          padding: 10,
                          background: "#fafafa",
                          border: "1px solid #eee",
                          borderRadius: 8,
                          fontSize: 12,
                          overflow: "auto"
                        }}
                      >
{JSON.stringify(h.details, null, 2)}
                      </pre>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
